import React, { useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Badge, Button } from 'reactstrap';
import moment from 'moment';
import { Health, Utils } from '../../services';
import {
    build_health_check_resume_snooze_signature,
    is_health_check_resume_snoozed,
    persist_health_check_resume_snooze,
} from '../../services/healthCheckResumeSnooze';
import {
    clear_health_resume_toast_offset,
    publish_health_resume_toast_offset,
} from '../../services/healthCheckResumeOffset';
import { pickInstanceIdFromAuthUser } from '../../services/kronosPrefs/sessionIds';
import { updateUi, checkHealth } from '../../redux';

import './HealthCheckResume.scss';

const HealthCheckResume = () => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const ui_lang = i18n.resolvedLanguage || i18n.language;
    const resume_ref = useRef(null);

    const { log, isLoading } = useSelector(state => state.health);
    const { showHealthChecks } = useSelector(state => state.ui);
    const user = useSelector(state => state.auth.user);
    const instance_id = useMemo(() => pickInstanceIdFromAuthUser(user), [user]);
    const [dismissed_signature, set_dismissed_signature] = useState(null);
    const [, bump_snooze_revision] = useReducer(revision => revision + 1, 0);

    const failed_groups = useMemo(() => {
        return Object.keys(log?.results || {})
            .map((key) => {
                const count = (log.results[key] || []).filter(tst => !tst.ignored && !tst.result).length;
                return { key, count };
            })
            .filter(group => group.count > 0);
    }, [log]);

    const nb_failed = useMemo(
        () => failed_groups.reduce((nb, group) => nb + group.count, 0),
        [failed_groups]
    );

    const failure_signature = useMemo(() => {
        if (!nb_failed)
            return '';
        const groups = failed_groups.map(group => `${group.key}:${group.count}`).join('|');
        return `${log?._id || log?.created_at || ''}|${groups}`;
    }, [failed_groups, log, nb_failed]);

    // Snooze until tomorrow: instance + failure groups (not the log id).
    // Same errors after re-check stay hidden; new errors or another instance show again.
    const snooze_signature = useMemo(() => {
        if (!nb_failed)
            return '';
        const groups = failed_groups.map(group => `${group.key}:${group.count}`).join('|');
        return build_health_check_resume_snooze_signature(instance_id, groups);
    }, [failed_groups, instance_id, nb_failed]);

    const is_dismissed = !!failure_signature && dismissed_signature === failure_signature;
    const is_snoozed = is_health_check_resume_snoozed(snooze_signature, Date.now(), instance_id);

    const dismiss_toast = () => {
        if (failure_signature)
            set_dismissed_signature(failure_signature);
    };

    const show_health_checks_panel = () => {
        dismiss_toast();
        dispatch(updateUi({ showHealthChecks: true }));
    };

    // Opening the panel from the top-bar icon also dismisses the toast.
    useEffect(() => {
        if (showHealthChecks && failure_signature)
            set_dismissed_signature(failure_signature);
    }, [showHealthChecks, failure_signature]);

    const check = (event) => {
        event.stopPropagation();
        dispatch(checkHealth());
    };

    const remind_tomorrow = (event) => {
        event.stopPropagation();
        if (!snooze_signature)
            return;
        persist_health_check_resume_snooze(snooze_signature, new Date(), instance_id);
        bump_snooze_revision();
    };

    const title = useMemo(() => {
        if (!nb_failed || !log?.created_at)
            return t('health.system_detected_error', { count: nb_failed });

        const m = moment(log.created_at);
        if (!m.isValid())
            return t('health.system_detected_error', { count: nb_failed });

        const locale = Utils.momentLocale(ui_lang);
        const localized = m.clone().locale(locale);
        const time = localized.format('LT');
        if (localized.isSame(moment(), 'day')) {
            return t('health.system_detected_error_today', { count: nb_failed, time });
        }
        return t('health.system_detected_error_on', {
            count: nb_failed,
            date: localized.format('L'),
            time,
        });
    }, [log?.created_at, nb_failed, t, ui_lang]);

    const resume_visible = !!nb_failed && !is_dismissed && !is_snoozed && !showHealthChecks;

    // Fixed error toasts share the bottom-right corner. Publish the card's
    // height so those toasts stack above this warning instead of under it.
    useLayoutEffect(() => {
        if (!resume_visible)
            return undefined;
        const node = resume_ref.current;
        if (!node)
            return undefined;
        const publish = () => publish_health_resume_toast_offset(node);
        publish();
        const observer = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(publish)
            : null;
        observer?.observe(node);
        window.addEventListener('resize', publish);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', publish);
            clear_health_resume_toast_offset();
        };
    }, [resume_visible]);

    if (!resume_visible)
        return null;

    const see_label = t('health.see_error', { count: nb_failed });
    const check_label = t('health.check_again');
    const remind_tomorrow_label = t('health.remind_tomorrow');

    return (
        <div className="HealthCheckResume noPrint" ref={resume_ref}>
            <div
                className="alert alert-danger"
                role="status"
                onClick={show_health_checks_panel}
            >
                <div className="HealthCheckResume__header">
                    <span className="title-main">
                        <i className="fa fa-warning" aria-hidden="true" />
                        {title}
                    </span>
                    <div
                        className="HealthCheckResume__snooze"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="alert-link bg-transparent border-0 p-0 text-nowrap health-resume-snooze"
                            onClick={remind_tomorrow}
                        >
                            {remind_tomorrow_label}
                        </button>
                    </div>
                </div>
                <div className="failed-groups">
                    {failed_groups.map((group) => (
                        <Badge
                            key={group.key}
                            color="light"
                            className="failed-group-badge"
                            title={t('health.summary.failed', { count: group.count })}
                            onClick={(event) => {
                                event.stopPropagation();
                                show_health_checks_panel();
                            }}
                        >
                            <i className={`fa fa-${Health.groupIcon(group.key)}`} aria-hidden="true" />
                            <span className="failed-group-label">
                                {t(`health.groups.${group.key}.title`)}
                            </span>
                            <span className="failed-group-count">{group.count}</span>
                        </Badge>
                    ))}
                </div>
                <div
                    className="HealthCheckResume__actions"
                    onClick={(event) => event.stopPropagation()}
                >
                    <Button
                        size="sm"
                        color="info"
                        className="health-resume-action"
                        onClick={show_health_checks_panel}
                    >
                        <i className="fa fa-eye" aria-hidden="true" />
                        {' '}
                        {see_label}
                    </Button>
                    <Button
                        size="sm"
                        color="primary"
                        className="health-resume-action"
                        onClick={check}
                        disabled={isLoading}
                    >
                        <i className={`fa ${isLoading ? 'fa-spinner fa-spin' : 'fa-bolt'}`} aria-hidden="true" />
                        {' '}
                        {check_label}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default HealthCheckResume;
